import { cn } from '@pubstack/ui/utils/cn'
import { ComponentProps, ForwardedRef, forwardRef, ReactNode, useRef } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import Button from '~/components/Button'
import { FileDownload } from '~/components/FileDownload'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'
import mergeRefs from '~/utils/mergeRefs'

type BaseFileTransferProps = WithClassName &
  ComponentProps<'input'> & {
    label: string
    trackingPrefixAndLabel: `${prefix}-${label}`
    filename?: string
    children?: ReactNode | ReactNode[]
    isDownloading: boolean
    onDownload: (filename: string) => void
    onDelete: (filename: string) => void
    onDownloadTemplate?: () => void
  }
const UnstyledFileTransfer = (
  { className, label, trackingPrefixAndLabel, filename, children, isDownloading, onDownload, onDelete, disabled, onDownloadTemplate, ...props }: BaseFileTransferProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUploadClick = () => {
    inputRef.current?.click()
  }

  const handleDeleteClick = (filename: string) => {
    onDelete(filename)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <input {...props} ref={mergeRefs([inputRef, ref])} type={'file'} accept={'text/csv'} className={'hidden'} />
      <FileDownload label={label} filenamePlaceholder="no file" filename={filename} trackingLabel={`${trackingPrefixAndLabel}-download`} isDownloading={isDownloading} onDownload={onDownload} />
      {filename ? (
        <div className={'flex gap-4'}>
          <Button disabled={disabled} onClick={() => handleDeleteClick(filename)} variant={'secondary'} iconName={'delete'} trackingLabel={`${trackingPrefixAndLabel}-delete`} />
          {children}
        </div>
      ) : (
        <div className={'flex gap-4'}>
          <Button disabled={disabled} onClick={handleUploadClick} variant={'primary'} iconName={'upload'} trackingLabel={`${trackingPrefixAndLabel}-upload`}>
            Upload csv
          </Button>
          {onDownloadTemplate && (
            <Button variant="secondary" iconName="download" trackingLabel={`${trackingPrefixAndLabel}-download`} onClick={onDownloadTemplate}>
              Download template
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export const BaseFileTransfer = forwardRef<HTMLInputElement, BaseFileTransferProps>(UnstyledFileTransfer)

type FileTransferProps<T extends FieldValues> = Omit<UseControllerProps<T>, 'defaultValue'> & Omit<BaseFileTransferProps, 'value' | 'ref'>
export const FileTransfer = <T extends FieldValues>({ name, control, rules, shouldUnregister, onChange, ...props }: FileTransferProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange: fieldOnChange, value: _value, ...field } }) => (
        <BaseFileTransfer
          {...props}
          {...field}
          onChange={(event) => {
            fieldOnChange(event.target.files?.[0]?.name ?? '')
            onChange && onChange(event)
          }}
        />
      )}
    />
  )
}
